import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  Applications,
  WhyChooseLiftile,
  TechnicalSpecificationTable,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  ArrowUp,
  Award,
  BadgeCheck,
  Building,
  Building2,
  Cable,
  ClipboardCheck,
  Cog,
  Factory,
  Flag,
  Headphones,
  Home,
  Landmark,
  Layers,
  Package,
  Rocket,
  ShieldCheck,
  Sparkles,
  Warehouse,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   CONVENTIONAL TOWER HOIST (LTC Series) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file — no JSX duplication.
   ===================================================================== */

const HERO_IMAGE = "/conventional-tower-hoist-site-2.jpeg";

const HERO_SPECS = [
  { Icon: ArrowUp, value: "Up to 65 m", label: "Maximum Lifting Height" },
  { Icon: Package, value: "Up to 1500 kg", label: "Gross Load Capacity" },
  { Icon: Zap, value: "30–35 m/min", label: "Lifting Speed" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const GALLERY = [
  {
    src: "/conventional-hoist-new-1.png",
    caption: "LIFTILE LTC Series on site — full setup with material trolley and cage",
  },
  {
    src: "/conventional-hoist-new-2.png",
    caption: "LTC Series mounted alongside multi-storey building — high-rise deployment",
  },
  {
    src: "/conventional-hoist-new-3.png",
    caption: "LTC Series winch drum with Usha Martin wire rope — factory close-up",
  },
  {
    src: "/conventional-hoist-new-4.png",
    caption: "LTC Series with LIFTILE-fabricated mast sections at SRS Industrial Park, Vadodara",
  },
  {
    src: "/conventional-tower-hoist-site-2.jpeg",
    caption: "LIFTILE LTC Series in operation — high-rise construction site",
  },
  {
    src: "/conventional-tower-hoist-site-1.jpeg",
    caption: "LTC Series mobile base — commercial building project",
  },
];

const QUICK_SPECS = [
  { label: "Gross Load", value: "750–1500 kg" },
  { label: "Maximum Height", value: "40–65 m" },
  { label: "Lifting Speed", value: "30–35 m/min" },
  { label: "Motor Power", value: "10–16 HP" },
];

const MODELS = {
  LTC750: {
    name: "LTC750",
    tagline: "Entry model for mid-rise builders",
    rows: [
      { label: "Gross Load", value: "750 kg" },
      { label: "Net Load", value: "500 kg" },
      { label: "Wire Rope", value: "12 mm (6×36)" },
      { label: "Motor", value: "10 HP" },
      { label: "Speed", value: "30–35 m/min" },
      { label: "Maximum Height", value: "40 m" },
      { label: "Mast Section", value: "380 × 380 × 3000 mm" },
      { label: "Section Profile", value: "50 × 50 × 6 mm" },
    ],
  },
  LTC1000: {
    name: "LTC1000",
    tagline: "Best-selling model for residential towers",
    rows: [
      { label: "Gross Load", value: "1000 kg" },
      { label: "Net Load", value: "750 kg" },
      { label: "Wire Rope", value: "12 mm (6×36)" },
      { label: "Motor", value: "12.5 HP" },
      { label: "Speed", value: "30–35 m/min" },
      { label: "Maximum Height", value: "60 m" },
      { label: "Mast Section", value: "380 × 380 × 3000 mm" },
      { label: "Front Profile", value: "50 × 50 × 6 mm" },
      { label: "Rear Profile", value: "50 × 50 × 6 mm" },
    ],
  },
  LTC1500: {
    name: "LTC1500",
    tagline: "Heavy-duty flagship for high-rise projects",
    rows: [
      { label: "Gross Load", value: "1500 kg" },
      { label: "Net Load", value: "1200 kg" },
      { label: "Wire Rope", value: "12 mm (6×36)" },
      { label: "Motor", value: "16 HP" },
      { label: "Speed", value: "30–35 m/min" },
      { label: "Maximum Height", value: "65 m" },
      { label: "Mast Section", value: "380 × 380 × 3000 mm" },
      { label: "Front Profile", value: "50 × 50 × 8 mm" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Rocket,
    title: "No Foundation Required",
    desc: "Quick installation without a concrete foundation, reducing setup time and project costs.",
  },
  {
    Icon: Layers,
    title: "Heavy Duty Mast Structure",
    desc: "Engineered using high-strength steel sections for excellent rigidity and long service life.",
  },
  {
    Icon: Sparkles,
    title: "Premium Epoxy Paint Finish",
    desc: "Epoxy Primer + Premium Gloss Epoxy Top Coat for Long-Lasting Corrosion Protection.",
  },
  {
    Icon: BadgeCheck,
    title: "Premium Branded Components",
    desc: "Built using trusted components from leading brands including Usha Martin, Crompton, ABB, Schneider Electric, FAG Bearings, Elecon and Polycab.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every machine is personally inspected and tested before dispatch to ensure quality, safety and reliable performance.",
  },
  {
    Icon: ShieldCheck,
    title: "Heavy Duty Steel Mast Construction",
    desc: "High-strength structural steel mast sections engineered for superior rigidity, stability and long service life on demanding sites.",
  },
  {
    Icon: Cable,
    title: "Usha Martin Wire Rope",
    desc: "High-quality ISI-certified wire rope offering superior strength, durability and reliable lifting performance.",
  },
  {
    Icon: Wrench,
    title: "Easy Maintenance",
    desc: "Simple mechanical design with easy access for routine inspection and maintenance, reducing downtime.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Buildings",
    desc: "Reliable material lifting for apartment complexes, villas and residential towers.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Efficient vertical transport for offices, malls, hotels and mixed-use developments.",
  },
  {
    Icon: Factory,
    title: "Industrial Projects",
    desc: "Dependable lifting for plants, factories and heavy industrial installations.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Projects",
    desc: "Trusted performance on bridges, metros, treatment plants and public infrastructure.",
  },
  {
    Icon: Building,
    title: "High-Rise Construction",
    desc: "Robust vertical lifting for tall towers and structural steel projects.",
  },
  {
    Icon: Warehouse,
    title: "Warehouse & Factory Construction",
    desc: "Fast material movement for large-format warehouses and factory sheds.",
  },
];

const TRUST_CARDS = [
  {
    Icon: Award,
    title: "Premium Manufacturing",
    desc: "Manufactured using high-quality steel, precision fabrication and strict quality standards.",
  },
  {
    Icon: ClipboardCheck,
    title: "Quality Inspection",
    desc: "Every machine undergoes testing and owner inspection before dispatch.",
  },
  {
    Icon: Cog,
    title: "Premium Components",
    desc: "We use trusted branded components to maximize performance and durability.",
  },
  {
    Icon: ShieldCheck,
    title: "Safety First",
    desc: "Designed with reliable safety mechanisms and operator protection in mind.",
  },
  {
    Icon: Headphones,
    title: "Reliable After-Sales Support",
    desc: "Dedicated technical assistance, spare parts support and service guidance whenever required.",
  },
  {
    Icon: Flag,
    title: "Proudly Made in India",
    desc: "Designed and manufactured in India for Indian construction conditions.",
  },
];

const TECH_SPECS = [
  { parameter: "Rated Gross Load", values: ["750 kg", "1000 kg", "1500 kg"] },
  { parameter: "Recommended Payload", values: ["500 kg", "750 kg", "1200 kg"] },
  {
    parameter: "Wire Rope",
    values: ["12 mm (6×36)", "12 mm (6×36)", "12 mm (6×36)"],
  },
  { parameter: "Electric Motor", values: ["10 HP", "12 HP", "16 HP"] },
  {
    parameter: "Lifting Speed",
    values: ["30–35 m/min", "30–35 m/min", "30–35 m/min"],
  },
  { parameter: "Maximum Height", values: ["40 m", "60 m", "65 m"] },
  {
    parameter: "Mast Section",
    values: [
      "380 × 380 × 3000 mm\n50 × 50 × 6 mm",
      "380 × 380 × 3000 mm\nFront  50 × 50 × 6 mm\nRear  50 × 50 × 6 mm",
      "380 × 380 × 3000 mm\nFront  50 × 50 × 8 mm\nRear  50 × 50 × 8 mm",
    ],
  },
];

const FAQS = [
  {
    q: "What is the maximum lifting height?",
    a: "Depending on the selected model, the LTC Series can safely operate up to 65 metres.",
  },
  {
    q: "What is the difference between Gross Load and Recommended Payload?",
    a: "Gross Load refers to the total lifting capacity, while Recommended Payload represents the safe working material load after considering the lifting accessories.",
  },
  {
    q: "Is a concrete foundation required?",
    a: "No.\n\nThe LTC Series is designed for quick installation without requiring a permanent concrete foundation.",
  },
  {
    q: "What maintenance is required?",
    a: "Routine inspection of wire ropes, safety mechanisms, lubrication points and electrical systems is recommended for reliable operation.",
  },
  {
    q: "Do you provide installation and technical support?",
    a: "Yes.\n\nLIFTILE provides installation guidance, technical support and after-sales assistance across India.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "multi-functional-tower-hoist",
    name: "Multi Functional Tower Hoist",
    desc: "Versatile hoist for materials and light personnel transport — flexible attachments for the whole project lifecycle.",
    image: "/product-multifunc-hoist.jpg",
  },
  {
    slug: "passenger-cum-material-hoist",
    name: "Passenger Cum Material Hoist",
    desc: "Twin-cage hoist for safe transport of personnel and materials up to 150 m on high-rise construction sites.",
    image: "/product-passenger-hoist.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact mini jib crane for slab-level material movement — ideal for tight sites and finishing phases.",
    image: "/product-monkey-lift.jpg",
  },
];

const TRUST_BADGES = [
  "Premium Branded Components",
  "Owner Inspected",
  "Made in India",
  "Nationwide Support",
];

/* ---------- Blueprint SVG for Section 8 (Download Brochure) ---------- */
const CTH_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    <rect x="180" y="40" width="40" height="320" strokeDasharray="4 4" />
    <line x1="180" y1="90" x2="220" y2="90" />
    <line x1="180" y1="140" x2="220" y2="140" />
    <line x1="180" y1="190" x2="220" y2="190" />
    <line x1="180" y1="240" x2="220" y2="240" />
    <line x1="180" y1="290" x2="220" y2="290" />
    <line x1="180" y1="40" x2="220" y2="90" />
    <line x1="220" y1="40" x2="180" y2="90" />
    <line x1="180" y1="90" x2="220" y2="140" />
    <line x1="220" y1="90" x2="180" y2="140" />
    <line x1="180" y1="140" x2="220" y2="190" />
    <line x1="220" y1="140" x2="180" y2="190" />
    <line x1="180" y1="190" x2="220" y2="240" />
    <line x1="220" y1="190" x2="180" y2="240" />
    <line x1="180" y1="240" x2="220" y2="290" />
    <line x1="220" y1="240" x2="180" y2="290" />
    <rect x="140" y="360" width="120" height="24" />
    <line x1="140" y1="384" x2="260" y2="384" />
    <rect x="118" y="220" width="60" height="60" strokeDasharray="2 3" />
    <line x1="118" y1="250" x2="178" y2="250" />
    <line x1="148" y1="220" x2="148" y2="280" />
    <line x1="80" y1="40" x2="80" y2="360" strokeDasharray="2 4" />
    <text
      x="60"
      y="205"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.6"
    >
      65m
    </text>
  </svg>
);

export default function ConventionalTowerHoistPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Conventional Tower Hoist" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="cth-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LTC Series"
        title={
          <>
            Conventional
            <br />
            Tower Hoist
            <br />
            <span className="text-liftile-orange">(Wire Rope)</span>
          </>
        }
        subtitle="Engineered for dependable vertical material lifting with robust construction, premium components and reliable performance for residential, commercial and industrial construction projects."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        titleSizeClass="text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] xl:text-[4.5rem]"
        overlayGradient="bg-gradient-to-r from-liftile-black/95 via-liftile-black/75 to-liftile-black/40"
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="cth-gallery"
        heading={
          <>
            Seen In <span className="text-liftile-orange">Real Projects</span>
          </>
        }
        subtitle="A closer look at LIFTILE LTC Series tower hoists deployed on live construction sites across India."
        items={GALLERY}
        seriesLabel="LTC Series"
        imageAlt="LIFTILE LTC Series Conventional Tower Hoist"
      />

      {/* ─── SECTION 3: QUICK SPECS ─── */}
      <QuickSpecsTabs
        testId="cth-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LTC750, LTC1000 and LTC1500 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LTC1000"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="cth-features"
        heading={
          <>
            Built for Performance.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Reliability.
            </span>
          </>
        }
        subtitle="Every LIFTILE Conventional Tower Hoist is designed using premium materials, trusted branded components and strict quality inspections to deliver dependable lifting performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: IDEAL APPLICATIONS ─── */}
      <Applications
        testId="cth-applications"
        eyebrow="Ideal Applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Construction Project</span>
          </>
        }
        subtitle="Designed to support safe and efficient material lifting across a wide range of construction environments."
        items={APPLICATIONS}
        variant="dark"
      />

      {/* ─── SECTION 6: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="cth-why-choose"
        image="/conventional-tower-hoist-site-1.jpeg"
        imageAlt="LIFTILE LTC Series Conventional Tower Hoist in operation"
        imageCaption="LTC Series delivering reliable material lifting on a live construction project."
        floatingBadge={{ top: "Made in India", bottom: "Since 2003" }}
        heading={
          <>
            Why Builders <br className="hidden sm:block" />
            Choose <span className="text-liftile-orange">LIFTILE</span>
          </>
        }
        subtitle="Built on decades of engineering experience, every LIFTILE machine is manufactured with a commitment to quality, reliability and long-term customer satisfaction."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 7: TECHNICAL SPECIFICATIONS ─── */}
      <TechnicalSpecificationTable
        testId="cth-tech-specs"
        subtitle="Compare all LTC Series models and choose the ideal solution for your project requirements."
        heading={
          <>
            Technical{" "}
            <span className="text-liftile-orange">Specifications</span>
          </>
        }
        columns={["LTC750", "LTC1000", "LTC1500"]}
        rows={TECH_SPECS}
        minWidthClass="min-w-[720px]"
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="cth-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete technical catalogue including specifications, drawings, features and product information."
        blueprintSvg={CTH_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="cth-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders and contractors most often ask about the LTC Series."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="cth-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11: PRODUCT CTA ─── */}
      <ProductFinalCTA
        testId="cth-product-cta"
        backgroundImage="/project-thiruvananthapuram.png"
        backgroundAlt="LIFTILE construction machinery on a live project site"
        heading={
          <>
            Ready to Lift{" "}
            <span className="text-liftile-orange">Smarter?</span>
          </>
        }
        subtitle="Talk to our engineering team and find the right Conventional Tower Hoist for your next construction project."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Talk to Our Sales Team", href: "/contact" }}
        trustBadges={TRUST_BADGES}
        overlayGradient="bg-gradient-to-b from-liftile-black/85 via-liftile-black/75 to-liftile-black/95"
      />

      <Footer />
    </>
  );
}
